import IconButton from '@components/Common/IconButton.js';
import Search from '@components/Common/Search.js';
import { IS_MOBILE } from '@constants/global.js';
import { CollapsePreviewIcon, ExpandPreviewIcon } from '@icons/IconLibrary.js';
import PreviewState from '@states/Preview.js';
import ProjectsState from '@states/Projects.js';
import { UIButton, UIDiv } from '@ui';
import ProjectColumnFilter from './ProjectColumnFilter.js';
import ProjectDataActions from './ProjectDataActions.js';
import ProjectTypePicker from './ProjectTypePicker.js';

export default class ProjectHeader extends UIDiv {
  constructor({
    projectType = 'personal',
    handleProjectTypeChange = () => {},
    onAddProject = () => {},
    onSearch = () => {},
    viewMode = 'list',
    onViewModeChange = () => {},
    isAnonymous = false,
    accessControl = null,
    currentFolderId,
  }) {
    super();
    this.projectType = projectType;
    this.handleProjectTypeChange = handleProjectTypeChange;
    this.onAddProject = onAddProject;
    this.onSearch = onSearch;
    this.viewMode = viewMode;
    this.onViewModeChange = onViewModeChange;
    this.isAnonymous = isAnonymous;
    this.accessControl = accessControl;
    this.currentFolderId = currentFolderId;
    this.headerOptionsVisible = true; // Track if header options are visible
    this.setId('project-header');
    this.setClass(
      'flex flex-wrap items-center gap-x-2.5 gap-y-2 md:gap-3 mb-4 relative md:pr-8',
    );

    window.addEventListener('previewSidebarEvent', () => {
      this.#adjustWidthForHeaderWrap();
      // show the expand button when preview is expanded
      this.expandToggleButton?.removeClass('hidden');
    });

    this.render();
  }

  render() {
    // Dispose existing picker to clean up its OrganizationState subscription
    if (this.projectTypePicker) {
      this.projectTypePicker.dispose?.();
      this.projectTypePicker = null;
    }
    this.clear();
    if (this.isAnonymous) {
      // show login button
      const loginButton = new UIButton(i18next.t('common.login'))
        .setClass(
          'min-h-8 bg-[var(--o-hive-primary-80)] text-white rounded-lg m-0 py-2 px-3 cursor-pointer',
        )
        .onClick(() => (window.location.href = '/login'));
      this.add(loginButton);
      this.#createExpandButton();
      return; // If the user is anonymous, we don't render the header
    }

    if (this.projectType !== 'shared') {
      this.projectTypePicker = new ProjectTypePicker({
        projectType: this.projectType,
        handleProjectTypeChange: this.handleProjectTypeChange,
      });

      this.add(this.projectTypePicker);
    } else if (!this.isAnonymous) {
      // if shared view then show a button to go back to personal projects
      const backToPersonalButton = new UIButton(
        i18next.t('pages.projects.backToPersonal'),
      );
      backToPersonalButton.setClass(
        'min-h-8 bg-[var(--o-hive-primary-80)] text-white rounded-lg m-0 py-2 px-3 cursor-pointer',
      );
      backToPersonalButton.onClick(() => {
        // Navigate back to /projects using window.location
        this.setProjectType('personal');
        this.handleProjectTypeChange('personal');
        // I know this is not the best way to handle routing, but there is a lot of state to reset so we just reload the page to be sure everything is reset
        window.location.href = '/projects';
      });

      this.add(backToPersonalButton);
    }

    this.add(new UIDiv().setClass('h-10 w-[1px] bg-gray-100')); // Vertical divider

    const shouldShowHeaderOptions = this.shouldShowHeaderOptions();
    this.headerOptionsVisible = shouldShowHeaderOptions;

    // Initialize permissions
    if (shouldShowHeaderOptions) {
      const canManage = this.accessControl.hasPermission('update');
      const canCreate = this.accessControl.hasPermission('create');

      if (canCreate && this.projectType !== 'shared') {
        const addProjectBtn = new IconButton({
          buttonClass: 'min-h-8 bg-[var(--o-hive-primary-80)] md:pr-[13px]',
          textClass: 'text-white',
          iconName: 'plus',
          text: IS_MOBILE() ? '' : i18next.t('pages.projects.newProject'),
          iconSize: IS_MOBILE() ? 21 : 16,
          strokeColor: 'white',
          onClick: () => this.onAddProject(),
        });

        this.add(addProjectBtn);
      }

      // Add project view mode selector
      // this.projectViewMode = new ProjectViewMode(
      //   this.viewMode,
      //   this.onViewModeChange,
      // );
      // this.add(this.projectViewMode);

      // Add column filter if the user can manage projects
      if (canManage && this.projectType !== 'shared') {
        this.columnFilter = new ProjectColumnFilter(
          this.currentFolderId,
          this.accessControl,
        );
        this.add(this.columnFilter);
      }

      // project data actions
      if (this.projectType !== 'shared') {
        this.projectDateActions = new ProjectDataActions();
        this.add(this.projectDateActions);
      }

      // Add search input
      this.searchInput = new Search({
        handleSearch: this.onSearch,
        className: `${IS_MOBILE() ? 'flex-grow' : ''}`,
      });
      this.add(this.searchInput);

      requestAnimationFrame(() => {
        this.#adjustWidthForHeaderWrap();
      }); // Adjust width after rendering
    }

    this.#createExpandButton();
  }

  /**
   * Determine if the project header options should be shown
   * Only show if current organization has valid subscription or user is on personal projects
   */
  shouldShowHeaderOptions() {
    // Always show for personal projects
    if (this.projectType === 'personal') {
      return true;
    }
    // For team projects, check subscription status
    // We check if there's a subscription error which indicates expired/invalid subscription
    return !ProjectsState.projects.subscriptionError;
  }

  /**
   * Update the column filter with a new current folder ID
   * Call this when navigating between folders
   * @param {string|null} newFolderId - The new folder ID (null for root projects)
   */
  updateColumnFilter(newFolderId, isProjectTypeChange = false) {
    if (!this.shouldShowHeaderOptions()) return;
    if (this.columnFilter && this.columnFilter.updateCurrentFolderId) {
      this.columnFilter.updateCurrentFolderId(newFolderId, isProjectTypeChange);
    }

    // Update stored currentFolderId for reference
    this.currentFolderId = newFolderId;
  }

  #createExpandButton() {
    this.expandToggleButton = new UIDiv();
    this.expandToggleButton.setId('expand-projects-toggle');
    this.expandToggleButton.setClass(
      `absolute top-2 right-1 w-6 h-6 cursor-pointer ${IS_MOBILE() ? 'hidden' : ''}`,
    );

    PreviewState.subscribe(state => {
      this.#updateExpandButton(state?.previewExpanded ?? false);
    });

    this.expandToggleButton.onClick(() => {
      PreviewState.setPreview({
        previewExpanded: !PreviewState.preview?.previewExpanded,
      });
      window.dispatchEvent(new Event('preview:expand'));
    });

    // Initial render of the button.
    this.#updateExpandButton(PreviewState.preview?.previewExpanded ?? false);
  }

  #updateExpandButton = isExpanded => {
    const Icon = isExpanded ? CollapsePreviewIcon : ExpandPreviewIcon;
    this.expandToggleButton.clear();
    this.expandToggleButton.add(new Icon({ size: 20, color: 'white' }));
    this.add(this.expandToggleButton);
  };

  setProjectType(projectType) {
    if (this.projectType === projectType) return;
    this.projectType = projectType;
  }
  setAccessControl(accessControl) {
    this.accessControl = accessControl;
  }

  #adjustWidthForHeaderWrap() {
    if (IS_MOBILE() || !this.searchInput) return; // No adjustment needed on mobile
    // Adjust width of the search input based on preview width, if less than 472px make width 120px
    const getContainer = document.getElementById('project-container');
    if (!getContainer) return;
    const previewWidth = getContainer.offsetWidth;
    if (previewWidth <= 472) {
      this.searchInput.setStyle('width', [`${previewWidth - 270}px`]);
    } else {
      this.searchInput.setStyle('width', ['auto']);
    }
  }
}
